/* eslint-disable @typescript-eslint/camelcase */
import React from 'react'
import styled from 'styled-components'
import { useForm, Controller, useWatch } from 'react-hook-form'
import {
  useRegister,
  useErrors,
  RegisterFormErrors,
  useGlobalLabels,
  useAcceptOnRegister,
} from '@sentecacommerce-theme/lib'
import {
  FormError,
  Button,
  Input,
  InputLabel as Label,
  IconLock,
} from '../../../..'
import { CTA } from '../Styled'
import { SocialLogin } from '../SocialLogin'
import { Checkbox } from '../Checkbox'

export const RegisterForm = ({ handleClose }: { handleClose: () => void }) => {
  const onSuccessfulReg = () => {
    accept(control.getValues().email)
    handleClose()
  }

  const [mutate, { isLoading, error }] = useRegister({
    onSuccess: onSuccessfulReg,
  })

  const {
    accept,
    clickSub,
    allSubscriptions,
    isAllFetching,
    checkedSubs,
    checkRequiredSubs,
  } = useAcceptOnRegister({})

  const {
    register,
    errorMessages,
    globalValidations,
    emailAddress,
    password: passwordLabels,
  } = useGlobalLabels()
  const apiErrors = useErrors<RegisterFormErrors>(error, {
    invalid_client: errorMessages?.somethingWentWrong,
    invitation_invalid: errorMessages?.somethingWentWrong,
    default: errorMessages?.somethingWentWrong,
    email_unavailable: errorMessages?.emailIsAlreadyInUse,
  })

  const onSubmit = async ({ email, password }: any) => {
    mutate({ email, password })
  }

  const {
    handleSubmit,
    errors,
    control,
    setError,
    clearErrors,
    formState,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      passwordConfirm: '',
    },
    mode: 'onChange',
  })

  const hasErrors = !!Object.keys(errors).length

  const password = useWatch({
    control,
    name: 'password',
    defaultValue: '',
  })

  const passwordConfirm = useWatch({
    control,
    name: 'passwordConfirm',
    defaultValue: '',
  })

  const validatePasswordMatch = (passwordVal: string) =>
    passwordVal === password
      ? undefined
      : globalValidations?.passwordsDoNotMatch

  const onPasswordKeyUp = () => {
    if (!formState.isSubmitted) return

    const passwordError = validatePasswordMatch(passwordConfirm)

    if (!passwordError) {
      clearErrors('passwordConfirm')
      return
    }

    setError('passwordConfirm', {
      type: 'manual',
      message: passwordError,
    })
  }

  const disabledButton = hasErrors || !checkRequiredSubs()
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Label>
        {emailAddress}
        <Controller
          control={control}
          name="email"
          rules={{
            required: globalValidations?.fieldIsRequired,
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: globalValidations?.invalidEmail,
            },
          }}
          as={<Input id="register-email" />}
        />
        <FormError errors={errors} fieldName="email" />
      </Label>
      <Label>
        {passwordLabels?.label}
        <Controller
          control={control}
          name="password"
          rules={{
            required: globalValidations?.fieldIsRequired,
            minLength: {
              value: 8,
              message: globalValidations?.passwordLength,
            },
          }}
          as={
            <Input
              id="register-pass"
              type="password"
              onKeyUp={onPasswordKeyUp}
            />
          }
        />
        <FormError errors={errors} fieldName="password" />
      </Label>
      <Label>
        {passwordLabels?.reEnterPassword}
        <Controller
          control={control}
          name="passwordConfirm"
          rules={{
            required: globalValidations?.fieldIsRequired,
            validate: validatePasswordMatch,
          }}
          onKeyUp={onPasswordKeyUp}
          as={<Input id="register-pass-re" type="password" />}
        />
        <FormError errors={errors} fieldName="passwordConfirm" />
      </Label>
      {!isAllFetching &&
        allSubscriptions &&
        Object.keys(allSubscriptions).map((sub) => {
          return (
            <>
              <Checkbox
                key={sub}
                isChecked={checkedSubs?.[sub]}
                onChange={() => clickSub(sub)}
                size="normal"
              >
                {allSubscriptions[sub].label}
              </Checkbox>
              {allSubscriptions[sub].isRequired && (
                <Req>{globalValidations.fieldIsRequired}</Req>
              )}
            </>
          )
        })}
      <CTA>
        <Button
          icon={<IconLock />}
          iconPosition="right"
          variant={!disabledButton ? 'green' : 'disabled'}
          fullWidth
          type="submit"
          disabled={isLoading || disabledButton}
        >
          {register?.callToAction}
        </Button>
      </CTA>
      <FormError fieldName="invalid_client" errors={apiErrors} />
      <FormError fieldName="invitation_invalid" errors={apiErrors} />
      <FormError fieldName="email_unavailable" errors={apiErrors} />
      <FormError fieldName="default" errors={apiErrors} />
      <SocialLogin handleClose={handleClose} />
    </form>
  )
}

const Req = styled.p.attrs({
  className: 'ml-5',
})`
  color: ${({ theme }) => theme.palette.danger};
  margin: -0.5rem 0 0.5rem 0;
`
